import { supabase } from '@/integrations/supabase/client';
import { 
  TwoFactorAuth, 
  ApiKey, 
  DataExportJob, 
  ErpIntegration,
  TwoFactorSetupResponse,
  ApiKeyCreateRequest,
  DataExportRequest,
  ErpIntegrationRequest,
  ExportFilters
} from '@/types/security';

// 2FA Service
export class TwoFactorAuthService {
  static async setup2FA(): Promise<TwoFactorSetupResponse> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Generate 2FA secret
      const secret = this.generateSecret();
      const qrCodeUrl = this.generateQRCodeUrl(user.email!, secret);
      
      // Generate backup codes
      const backupCodes = this.generateBackupCodes();

      return {
        secret,
        qrCodeUrl,
        backupCodes
      };
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      throw error;
    }
  }

  static async enable2FA(secret: string, verificationCode: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify the code first
      const isValid = this.verifyTOTP(secret, verificationCode);
      if (!isValid) throw new Error('Invalid verification code');

      // Generate backup codes
      const backupCodes = this.generateBackupCodes();

      // Try to use database function first, fallback to direct update
      try {
        const { data, error } = await supabase
          .rpc('enable_2fa', { user_uuid: user.id, secret });

        if (error) throw error;
        return data;
      } catch (dbError) {
        console.warn('Database function not available, using direct update:', dbError);
        
        // Direct update to user_profiles with 2FA fields
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            two_factor_enabled: true,
            two_factor_secret: secret,
            two_factor_backup_codes: backupCodes,
            two_factor_setup_completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);

        if (updateError) throw updateError;
        return true;
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      throw error;
    }
  }

  static async disable2FA(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Try to use database function first, fallback to direct update
      try {
        const { data, error } = await supabase
          .rpc('disable_2fa', { user_uuid: user.id });

        if (error) throw error;
        return data;
      } catch (dbError) {
        console.warn('Database function not available, using direct update:', dbError);
        
        // Direct update to user_profiles with 2FA fields
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            two_factor_enabled: false,
            two_factor_secret: null,
            two_factor_backup_codes: null,
            two_factor_setup_completed_at: null,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);

        if (updateError) throw updateError;
        return true;
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      throw error;
    }
  }

  static async verifyBackupCode(backupCode: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Try to use database function first, fallback to direct update
      try {
        const { data, error } = await supabase
          .rpc('verify_2fa_backup_code', { 
            user_uuid: user.id, 
            backup_code: backupCode 
          });

        if (error) throw error;
        return data;
      } catch (dbError) {
        console.warn('Database function not available, using direct update:', dbError);
        
        // Direct update using dedicated column
        const { data: profile, error: fetchError } = await supabase
          .from('user_profiles')
          .select('two_factor_backup_codes')
          .eq('user_id', user.id)
          .single();

        if (fetchError) throw fetchError;
        if (!profile.two_factor_backup_codes) return false;

        const backupCodes = profile.two_factor_backup_codes;
        const codeIndex = backupCodes.indexOf(backupCode.toUpperCase());
        
        if (codeIndex === -1) return false;

        // Remove used backup code
        backupCodes.splice(codeIndex, 1);
        
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            two_factor_backup_codes: backupCodes,
            last_2fa_verification: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);

        if (updateError) throw updateError;
        return true;
      }
    } catch (error) {
      console.error('Error verifying backup code:', error);
      throw error;
    }
  }

  static async get2FAStatus(): Promise<TwoFactorAuth> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_profiles')
        .select('two_factor_enabled, two_factor_secret, two_factor_setup_completed_at, last_2fa_verification')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.warn('Could not fetch 2FA status:', error);
        return {
          enabled: false,
          setupCompletedAt: undefined,
          lastVerification: undefined
        };
      }

      return {
        enabled: data.two_factor_enabled || false,
        secret: data.two_factor_secret,
        setupCompletedAt: data.two_factor_setup_completed_at,
        lastVerification: data.last_2fa_verification
      };
    } catch (error) {
      console.error('Error getting 2FA status:', error);
      throw error;
    }
  }

  private static generateSecret(): string {
    // Generate a random 32-character base32 secret
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  private static generateBackupCodes(): string[] {
    // Generate 10 backup codes
    const codes: string[] = [];
    for (let i = 0; i < 10; i++) {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      codes.push(code);
    }
    return codes;
  }

  private static generateQRCodeUrl(email: string, secret: string): string {
    const issuer = 'GreenCard Sustainability Manager';
    const otpauth = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauth)}`;
  }

  private static verifyTOTP(secret: string, code: string): boolean {
    // This is a simplified TOTP verification
    // In production, use a proper TOTP library like 'otplib'
    // For now, we'll accept any 6-digit code for demo purposes
    return /^\d{6}$/.test(code);
  }
}

// API Key Service
export class ApiKeyService {
  static async createApiKey(request: ApiKeyCreateRequest): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log('Creating API key for user:', user.id);
      console.log('Request:', request);

      const { data, error } = await supabase
        .rpc('generate_api_key', {
          user_uuid: user.id,
          key_name: request.name,
          permissions: request.permissions
        });

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      console.log('API key created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error creating API key:', error);
      throw error;
    }
  }

  static async getApiKeys(): Promise<ApiKey[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting API keys:', error);
      throw error;
    }
  }

  static async revokeApiKey(apiKeyId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log('Revoking API key:', apiKeyId, 'for user:', user.id);

      // First check if the API key exists and belongs to the user
      const { data: existingKey, error: checkError } = await supabase
        .from('api_keys')
        .select('id, name, is_active')
        .eq('id', apiKeyId)
        .eq('user_id', user.id)
        .single();

      if (checkError) {
        console.error('Error checking API key:', checkError);
        throw new Error('API key not found or access denied');
      }

      if (!existingKey) {
        throw new Error('API key not found');
      }

      console.log('Found API key:', existingKey);

      // Update the API key to inactive
      const { error } = await supabase
        .from('api_keys')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', apiKeyId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('API key revoked successfully');
    } catch (error) {
      console.error('Error revoking API key:', error);
      throw error;
    }
  }

  static async getApiAccessLogs(): Promise<any[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('api_access_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting API access logs:', error);
      throw error;
    }
  }


}

// Data Export Service
export class DataExportService {
  static async createExportJob(request: DataExportRequest): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('create_data_export_job', {
          user_uuid: user.id,
          export_type: request.exportType,
          format: request.format,
          filters: request.filters
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating export job:', error);
      throw error;
    }
  }

  static async getExportJobs(): Promise<DataExportJob[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('data_export_jobs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting export jobs:', error);
      throw error;
    }
  }

  static async downloadExport(jobId: string): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('data_export_jobs')
        .select('download_url, status')
        .eq('id', jobId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      if (data.status !== 'completed') {
        throw new Error('Export job is not completed');
      }

      return data.download_url;
    } catch (error) {
      console.error('Error downloading export:', error);
      throw error;
    }
  }

  static async exportData(request: DataExportRequest): Promise<Blob> {
    try {
      // Create export job
      const jobId = await this.createExportJob(request);
      
      // For demo purposes, we'll simulate the export process
      // In production, this would be handled by a background job
      const exportData = await this.generateExportData(request);
      
      return exportData;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  private static async generateExportData(request: DataExportRequest): Promise<Blob> {
    // This is a simplified export function
    // In production, this would query the database and format the data
    
    const mockData = {
      exportType: request.exportType,
      format: request.format,
      timestamp: new Date().toISOString(),
      data: [
        { metric: 'Energy Consumption', value: 1000, unit: 'kWh' },
        { metric: 'Carbon Emissions', value: 500, unit: 'tCO2e' },
        { metric: 'Water Usage', value: 2000, unit: 'm³' }
      ]
    };

    if (request.format === 'json') {
      return new Blob([JSON.stringify(mockData, null, 2)], { type: 'application/json' });
    } else if (request.format === 'csv') {
      const csvContent = this.convertToCSV(mockData.data);
      return new Blob([csvContent], { type: 'text/csv' });
    } else {
      // For Excel and PDF, we'd use libraries like xlsx or jsPDF
      return new Blob([JSON.stringify(mockData)], { type: 'application/json' });
    }
  }

  private static convertToCSV(data: any[]): string {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value}"` : value;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  }
}

// ERP Integration Service
export class ErpIntegrationService {
  static async setupIntegration(request: ErpIntegrationRequest): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('setup_erp_integration', {
          user_uuid: user.id,
          integration_type: request.integrationType,
          integration_name: request.name,
          configuration: request.configuration,
          data_mapping: request.dataMapping
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error setting up ERP integration:', error);
      throw error;
    }
  }

  static async getIntegrations(): Promise<ErpIntegration[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('erp_integrations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting ERP integrations:', error);
      throw error;
    }
  }

  static async testConnection(integrationId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get integration details
      const { data: integration, error: fetchError } = await supabase
        .from('erp_integrations')
        .select('*')
        .eq('id', integrationId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      // Test connection based on integration type
      const isConnected = await this.testErpConnection(integration);
      
      // Update last sync time
      await supabase
        .from('erp_integrations')
        .update({ last_sync_at: new Date().toISOString() })
        .eq('id', integrationId);

      return isConnected;
    } catch (error) {
      console.error('Error testing ERP connection:', error);
      throw error;
    }
  }

  static async syncData(integrationId: string): Promise<any> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get integration details
      const { data: integration, error: fetchError } = await supabase
        .from('erp_integrations')
        .select('*')
        .eq('id', integrationId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      // Start sync process
      const syncResult = await this.performDataSync(integration);
      
      // Log sync result
      await supabase
        .from('erp_sync_logs')
        .insert({
          integration_id: integrationId,
          user_id: user.id,
          sync_type: 'full_sync',
          status: syncResult.success ? 'success' : 'failed',
          records_processed: syncResult.recordsProcessed,
          records_created: syncResult.recordsCreated,
          records_updated: syncResult.recordsUpdated,
          records_failed: syncResult.recordsFailed,
          error_message: syncResult.errorMessage,
          completed_at: new Date().toISOString()
        });

      return syncResult;
    } catch (error) {
      console.error('Error syncing ERP data:', error);
      throw error;
    }
  }

  private static async testErpConnection(integration: ErpIntegration): Promise<boolean> {
    // This is a simplified connection test
    // In production, this would actually test the connection to the ERP system
    switch (integration.integrationType) {
      case 'sap':
        return this.testSapConnection(integration.configuration);
      case 'oracle':
        return this.testOracleConnection(integration.configuration);
      case 'microsoft_dynamics':
        return this.testDynamicsConnection(integration.configuration);
      case 'salesforce':
        return this.testSalesforceConnection(integration.configuration);
      default:
        return this.testCustomConnection(integration.configuration);
    }
  }

  private static async testSapConnection(config: any): Promise<boolean> {
    // Simulate SAP connection test
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  private static async testOracleConnection(config: any): Promise<boolean> {
    // Simulate Oracle connection test
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  private static async testDynamicsConnection(config: any): Promise<boolean> {
    // Simulate Microsoft Dynamics connection test
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  private static async testSalesforceConnection(config: any): Promise<boolean> {
    // Simulate Salesforce connection test
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  private static async testCustomConnection(config: any): Promise<boolean> {
    // Simulate custom ERP connection test
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  private static async performDataSync(integration: ErpIntegration): Promise<any> {
    // This is a simplified data sync
    // In production, this would actually sync data from the ERP system
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          success: true,
          recordsProcessed: 100,
          recordsCreated: 50,
          recordsUpdated: 30,
          recordsFailed: 0,
          errorMessage: null
        });
      }, 2000);
    });
  }
}

// Main Security Service
export class SecurityService {
  static twoFactor = TwoFactorAuthService;
  static apiKeys = ApiKeyService;
  static dataExport = DataExportService;
  static erpIntegration = ErpIntegrationService;

  static async getSecuritySettings(): Promise<any> {
    try {
      // Get 2FA status (this should work even without database columns)
      let twoFactorStatus;
      try {
        twoFactorStatus = await this.twoFactor.get2FAStatus();
      } catch (error) {
        console.warn('Error getting 2FA status:', error);
        twoFactorStatus = { enabled: false };
      }

      // Get other settings (these might fail if tables don't exist yet)
      let apiKeys = [];
      let exportJobs = [];
      let integrations = [];

      try {
        apiKeys = await this.apiKeys.getApiKeys();
      } catch (error) {
        console.warn('Error getting API keys:', error);
      }

      try {
        exportJobs = await this.dataExport.getExportJobs();
      } catch (error) {
        console.warn('Error getting export jobs:', error);
      }

      try {
        integrations = await this.erpIntegration.getIntegrations();
      } catch (error) {
        console.warn('Error getting ERP integrations:', error);
      }

      return {
        twoFactorAuth: twoFactorStatus,
        apiKeys,
        dataExportJobs: exportJobs,
        erpIntegrations: integrations
      };
    } catch (error) {
      console.error('Error getting security settings:', error);
      throw error;
    }
  }
} 